import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
  ActionIcon,
  Switch,
  Badge,
  Tooltip,
  Loader,
} from "@mantine/core";
import {
  IconUser,
  IconMinus,
  IconPlus,
  IconUsersGroup,
} from "@tabler/icons-react";

function EditMembersModal({
  isOpen,
  onClose,
  group,
  groupUsers = [],
  availableUsers = [],
  onMembersUpdated,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loadingStates] = useState({});
  const [error, setError] = useState(null);
  const [localGroupUsers, setLocalGroupUsers] = useState(groupUsers);
  const [localAvailableUsers, setLocalAvailableUsers] =
    useState(availableUsers);
  const [hasChanges, setHasChanges] = useState(false);
  const [isProcessingDownline, setIsProcessingDownline] = useState(false);

  // Sort users alphabetically by first and last name
  const sortUsersByName = users => {
    return [...users].sort((a, b) => {
      const nameA = `${a.first_name || ""} ${a.last_name || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.first_name || ""} ${b.last_name || ""}`
        .trim()
        .toLowerCase();

      // Fallback to name, email, or "Unknown User" if first/last name not available
      const displayNameA =
        nameA || (a.name || a.email || "Unknown User").toLowerCase();
      const displayNameB =
        nameB || (b.name || b.email || "Unknown User").toLowerCase();

      return displayNameA.localeCompare(displayNameB);
    });
  };

  // Update local state when props change
  useEffect(() => {
    setLocalGroupUsers(sortUsersByName(groupUsers));
    setLocalAvailableUsers(sortUsersByName(availableUsers));
    // Reset changes flag when modal opens with new data
    setHasChanges(false);
  }, [groupUsers, availableUsers]);

  const updateGroupMember = async (user, include) => {
    if (!group?.id || !user?.user_id) return;

    setError(null);

    // INSTANT UI UPDATE - Move user between lists immediately (optimistic)
    if (include) {
      // Adding user: move from available to group users
      const userToAdd = localAvailableUsers.find(
        u => u.user_id === user.user_id
      );
      if (userToAdd) {
        setLocalGroupUsers(prev => sortUsersByName([...prev, userToAdd]));
        setLocalAvailableUsers(prev =>
          sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
        );
      }
    } else {
      // Removing user: move from group users to available
      const userToRemove = localGroupUsers.find(
        u => u.user_id === user.user_id
      );
      if (userToRemove) {
        setLocalAvailableUsers(prev =>
          sortUsersByName([...prev, userToRemove])
        );
        setLocalGroupUsers(prev =>
          sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
        );
      }
    }

    setHasChanges(true);

    // Show loading if adding user and group has include_downline
    if (include && group?.include_downline) {
      setIsProcessingDownline(true);
    }

    // Fire API call in background without blocking UI
    (async () => {
      try {
        const jwt = await getVerifiedJWT();
        const response = await fetch(getApiEndpoint("/v1/groups/group/user"), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            group_id: group.id,
            user_id: user.user_id,
            include: include,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to update group members");
        }

        // If adding a user and group has include_downline, fetch updated data from server
        if (include && group?.include_downline) {
          // Make GET call to refresh the group data to get downline users
          const groupResponse = await fetch(
            getApiEndpoint(`/v1/groups/group-users/${group.id}`),
            {
              headers: {
                Authorization: `Bearer ${jwt}`,
              },
            }
          );

          if (groupResponse.ok) {
            const result = await groupResponse.json();
            // Update lists with fresh data from server
            setLocalGroupUsers(sortUsersByName(result.data.users || []));
            setLocalAvailableUsers(
              sortUsersByName(result.data.unadded_users || [])
            );
          }
        }
      } catch (err) {
        // If error occurs, revert the optimistic update
        if (include) {
          // Revert add: move user back to available
          const userToRevert = localGroupUsers.find(
            u => u.user_id === user.user_id
          );
          if (userToRevert) {
            setLocalAvailableUsers(prev =>
              sortUsersByName([...prev, userToRevert])
            );
            setLocalGroupUsers(prev =>
              sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
            );
          }
        } else {
          // Revert remove: move user back to group
          const userToRevert = localAvailableUsers.find(
            u => u.user_id === user.user_id
          );
          if (userToRevert) {
            setLocalGroupUsers(prev =>
              sortUsersByName([...prev, userToRevert])
            );
            setLocalAvailableUsers(prev =>
              sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
            );
          }
        }
        setError(err.message);
      } finally {
        setIsProcessingDownline(false);
      }
    })();
  };

  const handleAddUser = user => {
    updateGroupMember(user, true);
  };

  const handleRemoveUser = user => {
    updateGroupMember(user, false);
  };

  const handleToggleIncludeDownline = async user => {
    if (!user?.user_id || !group?.id) {
      setError(
        "Unable to toggle: required data not available. Try refreshing the page."
      );
      return;
    }

    const newIncludeDownlineValue = !user.include_downline;

    // INSTANT UI UPDATE - Toggle the switch immediately
    setLocalGroupUsers(prev =>
      prev.map(u =>
        u.user_id === user.user_id
          ? { ...u, include_downline: newIncludeDownlineValue }
          : u
      )
    );

    setHasChanges(true);
    setError(null);

    // Show loading indicator if enabling downline (background processing will add members)
    if (newIncludeDownlineValue) {
      setIsProcessingDownline(true);
    }

    // Fire API call in background without blocking UI
    (async () => {
      try {
        const jwt = await getVerifiedJWT();

        // Update the group-specific include_downline setting
        const response = await fetch(getApiEndpoint("/v1/groups/group/user"), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            group_id: group.id,
            user_id: user.user_id,
            include: true, // User is already in the group
            include_downline: newIncludeDownlineValue,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to update include downline setting");
        }

        // If enabling include_downline, refresh data from server to get newly added downline users
        // This happens in the background while user continues working
        if (newIncludeDownlineValue) {
          const groupUsersResponse = await fetch(
            getApiEndpoint(`/v1/groups/group-users/${group.id}`),
            {
              headers: {
                Authorization: `Bearer ${jwt}`,
              },
            }
          );

          if (groupUsersResponse.ok) {
            const result = await groupUsersResponse.json();
            setLocalGroupUsers(sortUsersByName(result.data.users || []));
            setLocalAvailableUsers(
              sortUsersByName(result.data.unadded_users || [])
            );
          }
        }
      } catch (err) {
        // If error occurs, revert the optimistic update
        setLocalGroupUsers(prev =>
          prev.map(u =>
            u.user_id === user.user_id
              ? { ...u, include_downline: !newIncludeDownlineValue }
              : u
          )
        );
        setError(err.message);
      } finally {
        // Hide loading indicator when background processing completes
        setIsProcessingDownline(false);
      }
    })();
  };

  const handleClose = () => {
    setError(null);

    // Only call the refresh callback if changes were made
    if (hasChanges && onMembersUpdated) {
      onMembersUpdated();
    }

    onClose();
  };

  const getUserDisplayName = user => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || "Unknown User";
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit Members - ${group?.name || "Group"}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {/* Error Alert */}
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        {/* Group Members Section */}
        <Paper p="md" withBorder>
          <Group justify="space-between" align="center" mb="md">
            <Group gap="xs">
              <Title order={4}>Group Members ({localGroupUsers.length})</Title>
              {isProcessingDownline && <Loader size="xs" color="grape" />}
            </Group>
            <Text size="xs" c="dimmed" fw={500}>
              Include Downline
            </Text>
          </Group>
          {localGroupUsers.length > 0 ? (
            <Stack gap="sm">
              {localGroupUsers.map((user, index) => (
                <Group
                  key={user.user_id || index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <IconUser size={16} color="var(--mantine-color-blue-6)" />
                  <Stack gap={1} style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={500} size="sm">
                        {getUserDisplayName(user)}
                      </Text>
                      {user.include_downline && (
                        <Badge
                          size="xs"
                          color="grape"
                          variant="light"
                          leftSection={<IconUsersGroup size={10} />}
                        >
                          Includes Downline
                        </Badge>
                      )}
                    </Group>
                    {user.email && (
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    )}
                    {user.role && (
                      <Text size="xs" c="dimmed">
                        {user.role}
                      </Text>
                    )}
                  </Stack>
                  <Tooltip
                    label={
                      user.include_downline
                        ? "Disable auto-add downline"
                        : "Enable auto-add downline"
                    }
                    position="left"
                  >
                    <Switch
                      size="sm"
                      checked={user.include_downline || false}
                      onChange={() => handleToggleIncludeDownline(user)}
                      disabled={loadingStates[`toggle-${user.user_id}`]}
                      color="grape"
                    />
                  </Tooltip>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveUser(user)}
                    loading={loadingStates[`${user.user_id}-false`]}
                    disabled={loadingStates[`${user.user_id}-false`]}
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No members in this group
            </Text>
          )}
        </Paper>

        {/* Available Users Section */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Available Users ({localAvailableUsers.length})
          </Title>
          {localAvailableUsers.length > 0 ? (
            <Stack gap="sm">
              {localAvailableUsers.map((user, index) => (
                <Group
                  key={user.user_id || index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <IconUser size={16} color="var(--mantine-color-gray-6)" />
                  <Stack gap={1} style={{ flex: 1 }}>
                    <Text fw={500} size="sm">
                      {getUserDisplayName(user)}
                    </Text>
                    {user.email && (
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    )}
                    {user.role && (
                      <Text size="xs" c="dimmed">
                        {user.role}
                      </Text>
                    )}
                  </Stack>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="green"
                    onClick={() => handleAddUser(user)}
                    loading={loadingStates[`${user.user_id}-true`]}
                    disabled={loadingStates[`${user.user_id}-true`]}
                  >
                    <IconPlus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No additional users available
            </Text>
          )}
        </Paper>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditMembersModal;
